/**
 * shadcn Tooltip — hover/focus tooltip using Base UI.
 *
 * Source: shadcn/ui `tooltip` pattern (base-nova style, Tailwind v4).
 * Adapted: uses `@base-ui/react/tooltip` instead of Radix UI.
 *
 * Usage:
 *   <TooltipProvider>
 *     <Tooltip>
 *       <TooltipTrigger asChild>
 *         <Button>Hover me</Button>
 *       </TooltipTrigger>
 *       <TooltipContent>
 *         <p>Tooltip text</p>
 *       </TooltipContent>
 *     </Tooltip>
 *   </TooltipProvider>
 *
 * For disabled button tooltips, wrap the button in a <span> and apply
 * TooltipTrigger to the span (disabled buttons don't fire pointer events).
 *
 * @see .claude/docs/chakra-to-shadcn-migration.md
 */
'use client';

import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip';
import { cn } from '@web-app/lib/utils';
import * as React from 'react';

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

function TooltipContent({
  className,
  sideOffset = 4,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Popup> & { sideOffset?: number }) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Positioner sideOffset={sideOffset}>
        <TooltipPrimitive.Popup
          data-slot="tooltip-content"
          className={cn(
            'z-50 overflow-hidden rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground',
            'data-[starting-style]:opacity-0 data-[ending-style]:opacity-0',
            'transition-opacity duration-150',
            className,
          )}
          {...props}
        >
          {children}
          <TooltipPrimitive.Arrow className="fill-primary" />
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };
